/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/Icon';
import { useUserId } from '@/api/hooks';
import { useIsInternalUser } from '@/lib/userRole';
import { getRoles, isDealerFromClaims } from '@/lib/tokenClaims';
import { hasInternalRole } from '@/lib/roles';
import {
  getSchematicProperties,
  getUserSettings,
  saveSchematicProperties,
  saveUserSettings,
  type SchematicProperties,
} from '@/api/settings';
import { Switch, type ResolvedTheme } from '@vaas/react-ui-kit';
import { toast } from '@/stores/toastStore';
import { useThemeToggle } from './useThemeToggle';
import { Modal } from './Modal';

const DISPLAY_SIZES = ['small', 'medium', 'large'] as const;

type DisplaySize = (typeof DISPLAY_SIZES)[number];

/**
 * The number each size travels as, and the mirror of `DisplaySize` in the schematics service.
 * `small` and `large` sit at the ends of the ranges the backend still validates (symbol 25-50,
 * wire 1-7, spacing 20-60) so that neither extreme becomes unreachable and a user who had pushed
 * a slider to its maximum is not written down on their next save; `medium` holds each field's
 * shipped default so a user who never touched the setting sees no change.
 */
const DISPLAY_SIZE_VALUES: Record<keyof SizeDraft, Record<DisplaySize, number>> = {
  symbolSize: { small: 25, medium: 35, large: 50 },
  wireSize: { small: 1, medium: 3, large: 7 },
  spacing: { small: 20, medium: 30, large: 60 },
};

interface SizeDraft {
  symbolSize: DisplaySize;
  wireSize: DisplaySize;
  spacing: DisplaySize;
}

interface Draft extends SizeDraft {
  showEngineeringLabels: boolean;
  showWireColors: boolean;
  showCommonViewTypes: boolean;
}

const DEFAULT_DRAFT: Draft = {
  symbolSize: 'medium',
  wireSize: 'medium',
  spacing: 'medium',
  showEngineeringLabels: false,
  showWireColors: true,
  showCommonViewTypes: true,
};

/** Nearest size to a stored number, so a value saved when these were sliders still opens. */
function sizeOf(field: keyof SizeDraft, stored: number | undefined): DisplaySize | undefined {
  if (typeof stored !== 'number' || !Number.isFinite(stored)) {
    return undefined;
  }
  const values = DISPLAY_SIZE_VALUES[field];
  return DISPLAY_SIZES.reduce((best, size) =>
    Math.abs(stored - values[size]) < Math.abs(stored - values[best]) ? size : best,
  );
}

function isDefaultDraft(draft: Draft): boolean {
  return (Object.keys(DEFAULT_DRAFT) as (keyof Draft)[]).every(
    (key) => draft[key] === DEFAULT_DRAFT[key],
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="set-row">
      <div className="set-label">{label}</div>
      {children}
    </div>
  );
}

function Segment<T extends string>({
  value,
  options,
  onChange,
  className,
}: {
  value: T;
  options: [T, string][];
  onChange: (v: T) => void;
  className?: string;
}) {
  return (
    <div className={className ? `seg ${className}` : 'seg'}>
      {options.map(([val, label]) => (
        <button
          key={val}
          className={`seg-btn${value === val ? ' on' : ''}`}
          onClick={() => onChange(val)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** Workspace preferences dialog: schematic display + app preferences, persisted to the backend. */
export function SettingsModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const gmin = useUserId();
  const queryClient = useQueryClient();
  // Engineering display codes are internal-only; persistence uses real token claims, not "view as".
  const isInternal = useIsInternalUser();
  // Theme edits go through the shared write path (persists + updates cache).
  const { resolved: theme, set: setTheme } = useThemeToggle();
  // Only the two concrete themes are offered; "system" is reachable from the OS, not from here.
  const themes: [ResolvedTheme, string][] = [
    ['light', t('settings.themeLight')],
    ['dark', t('settings.themeDark')],
  ];
  const sizes: [DisplaySize, string][] = [
    ['small', t('settings.sizeSmall')],
    ['medium', t('settings.sizeMedium')],
    ['large', t('settings.sizeLarge')],
  ];

  const enabled = Boolean(gmin);
  const settingsQuery = useQuery({
    queryKey: ['user-settings', gmin],
    queryFn: () => getUserSettings(gmin),
    enabled,
    retry: false,
  });
  const schematicQuery = useQuery({
    queryKey: ['schematic-properties', gmin],
    queryFn: () => getSchematicProperties(gmin),
    enabled,
    retry: false,
  });

  const [draft, setDraft] = useState<Draft>(DEFAULT_DRAFT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Sync schematic draft only; theme is restored on startup and edited live here.
    const p = schematicQuery.data;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDraft((prev) => ({
      symbolSize: sizeOf('symbolSize', p?.symbolSize) ?? prev.symbolSize,
      wireSize: sizeOf('wireSize', p?.wireSize) ?? prev.wireSize,
      spacing: sizeOf('spacing', p?.spacing) ?? prev.spacing,
      showEngineeringLabels: p?.showEngineeringLabels ?? prev.showEngineeringLabels,
      showWireColors: p?.showWireColors ?? prev.showWireColors,
      showCommonViewTypes: p?.showCommonViewTypes ?? prev.showCommonViewTypes,
    }));
  }, [schematicQuery.data]);

  const set =
    <K extends keyof Draft>(key: K) =>
    (value: Draft[K]) =>
      setDraft((d) => ({ ...d, [key]: value }));

  const sizeRow = (field: keyof SizeDraft, label: string) => (
    <Row label={label}>
      <Segment value={draft[field]} options={sizes} onChange={set(field)} className="seg-size" />
    </Row>
  );

  // Restores the shipped schematic display values into the draft. Appearance is left alone: it is
  // applied and persisted live by useThemeToggle, so resetting it here would take effect even if
  // the user then cancelled out of the dialog.
  const reset = () => {
    setError(null);
    setDraft(DEFAULT_DRAFT);
  };

  const save = async () => {
    if (!gmin) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Preserve language managed elsewhere.
      const current = settingsQuery.data;
      await saveUserSettings(gmin, {
        language: current?.languageDisplay?.languageCode ?? 'en',
        theme,
      });
      // Only pin to service code for real dealer-only tokens (dealer claims with no internal
      // group). Users who also hold an internal role — even if their token carries a dealer
      // role or partner id — keep the engineering option they were shown, so the toggle can be
      // switched back to engineering. Uses real claims, not the "view as" override.
      const dealerOnly = isDealerFromClaims() && !hasInternalRole(getRoles());
      const nextProps: SchematicProperties = {
        userId: gmin,
        symbolSize: DISPLAY_SIZE_VALUES.symbolSize[draft.symbolSize],
        wireSize: DISPLAY_SIZE_VALUES.wireSize[draft.wireSize],
        spacing: DISPLAY_SIZE_VALUES.spacing[draft.spacing],
        showEngineeringLabels: dealerOnly ? false : draft.showEngineeringLabels,
        showWireColors: draft.showWireColors,
        showCommonViewTypes: draft.showCommonViewTypes,
      };
      const saved = await saveSchematicProperties(gmin, nextProps);
      // Push the authoritative saved value straight into the shared cache so the explorer
      // tab lists, 2D render and MECL switch mode immediately. Relying only on a background
      // invalidate refetch left the lists showing the old mode until the next fetch resolved.
      // Overlay only the fields the server actually echoed on top of the complete payload we
      // just persisted: a partial/empty PUT response must never drop showEngineeringLabels (or
      // any other flag), which would silently revert all four tabs to the previous mode.
      const merged: SchematicProperties = { ...nextProps, ...(saved ?? {}) };
      queryClient.setQueryData(['schematic-properties', gmin], merged);
      void queryClient.invalidateQueries({ queryKey: ['user-settings', gmin] });
      toast.success(t('toast.settingsSaved'));
      onClose();
    } catch {
      setError(t('settings.error'));
      toast.error(t('settings.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} labelledBy="set-title">
      <div className="modal set-modal">
        <div className="modal-head">
          <Icon
            name="settings"
            size={22}
            style={{ color: 'var(--color-primary-text)', flex: 'none' }}
          />
          <div className="mh-titles">
            <h2 id="set-title">{t('settings.title')}</h2>
            <div className="mh-sub">{t('settings.subtitle')}</div>
          </div>
          <div className="sp" style={{ flex: 1 }} />
          <button className="iclose" onClick={onClose} aria-label={t('common.close')}>
            <Icon name="close" size={18} />
          </button>
        </div>

        <div className="ov-body set-body">
          <div className="set-sec">{t('settings.schematicDisplay')}</div>
          <div className="set-list">
            {sizeRow('symbolSize', t('settings.symbolSize'))}
            {sizeRow('wireSize', t('settings.wireSize'))}
            {sizeRow('spacing', t('settings.spacing'))}
            {isInternal && (
              <Row label={t('settings.displayCode')}>
                <Segment
                  value={draft.showEngineeringLabels ? 'engineering' : 'service'}
                  options={[
                    ['service', t('settings.serviceCode')],
                    ['engineering', t('settings.engineeringCode')],
                  ]}
                  onChange={(v) => set('showEngineeringLabels')(v === 'engineering')}
                />
              </Row>
            )}
            <Switch
              className="w-full justify-between"
              label={t('settings.coloredWires')}
              labelPosition="start"
              checked={draft.showWireColors}
              onCheckedChange={set('showWireColors')}
            />
            <Switch
              className="w-full justify-between"
              label={t('settings.highlightCommon')}
              labelPosition="start"
              checked={draft.showCommonViewTypes}
              onCheckedChange={set('showCommonViewTypes')}
            />
          </div>

          <div className="set-sec">{t('settings.preferences')}</div>
          <div className="set-list">
            <Row label={t('settings.appearance')}>
              <Segment value={theme} options={themes} onChange={setTheme} />
            </Row>
          </div>

          {error && <div className="set-error">{error}</div>}
        </div>

        <div className="ov-foot set-foot">
          <button
            className="set-foot-reset"
            onClick={reset}
            disabled={saving || isDefaultDraft(draft)}
            title={t('settings.resetHint')}
          >
            {t('settings.reset')}
          </button>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>
            {t('settings.cancel')}
          </button>
          <button className="btn btn-pri" onClick={save} disabled={saving}>
            {saving ? t('settings.saving') : t('settings.save')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
